import axios from 'axios'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { AuthError, NetworkError } from '../common/errors'

// Platform detection (no auth required)
export interface PlatformDetectResponse {
  platform_id: number | null
}

const detectPlatform = async (): Promise<PlatformDetectResponse> => {
  const response = await axios.get('/api/device-link/platform')
  return response.data
}

export const useDetectPlatform = () => {
  return useSuspenseQuery({
    queryKey: ['detectPlatform'],
    queryFn: detectPlatform,
    staleTime: Infinity,
  })
}

/**
 * Non-suspending variant for the TV route. Platform detection is a nicety
 * there, so a failure must not throw to the error boundary - on a TV without
 * network that surfaced a bogus "Authentication Error" screen with no way out.
 */
export const useDetectPlatformOptional = () => {
  return useQuery({
    queryKey: ['detectPlatform'],
    queryFn: detectPlatform,
    staleTime: Infinity,
    retry: false,
    throwOnError: false,
  })
}

/**
 * True when the request never reached the API (no network, DNS failure,
 * blocked route). These must be reported as connection problems and retried,
 * never as authentication failures.
 */
export const isConnectionError = (err: unknown): boolean => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false)
    return true
  if (err instanceof NetworkError) return true
  return err instanceof Error && err.name === 'NetworkError'
}

/** True when the API rejected our device token and re-linking is required. */
export const isAuthenticationError = (err: unknown): boolean => {
  if (err instanceof AuthError) return true
  const status = (err as ApiError)?.response?.status
  return status === 401 || status === 403
}

// Token storage utilities
const DEVICE_TOKEN_KEY = 'device_access_token'
const DEVICE_PLATFORM_ID_KEY = 'device_platform_id'

// The TV shells embed this page in a cross-origin iframe, where localStorage
// can be unavailable. Fall back to memory so a blocked store degrades to
// "re-link after restart" instead of throwing.
const memoryStore: Record<string, string | null> = {}

const storageGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch {
    return memoryStore[key] ?? null
  }
}

const storageSet = (key: string, value: string): void => {
  memoryStore[key] = value
  try {
    localStorage.setItem(key, value)
  } catch {
    // storage blocked - the memory copy is all we get
  }
}

const storageRemove = (key: string): void => {
  delete memoryStore[key]
  try {
    localStorage.removeItem(key)
  } catch {
    // storage blocked
  }
}

export const saveDeviceToken = (token: string, platformId?: number): void => {
  storageSet(DEVICE_TOKEN_KEY, token)
  if (platformId !== undefined) {
    storageSet(DEVICE_PLATFORM_ID_KEY, String(platformId))
  }
}

export const getStoredDeviceToken = (): string | null => {
  return storageGet(DEVICE_TOKEN_KEY)
}

export const getStoredDevicePlatformId = (): number | null => {
  const platformId = storageGet(DEVICE_PLATFORM_ID_KEY)
  return platformId ? Number(platformId) : null
}

export const clearDeviceToken = (): void => {
  storageRemove(DEVICE_TOKEN_KEY)
  storageRemove(DEVICE_PLATFORM_ID_KEY)
  activeDeviceToken = null
  activeDevicePlatformId = null
}

// In-memory active device session — per-tab, not shared across tabs via localStorage.
// Only the /tv page activates this; regular login tabs never set it, so the
// interceptor won't send the Bearer token outside of the TV context.
let activeDeviceToken: string | null = null
let activeDevicePlatformId: number | null = null

export const activateDeviceSession = (
  token: string,
  platformId?: number
): void => {
  activeDeviceToken = token
  activeDevicePlatformId = platformId ?? null
}

export const getActiveDeviceToken = (): string | null => activeDeviceToken

export const getActiveDevicePlatformId = (): number | null =>
  activeDevicePlatformId

export interface ApiError {
  response?: {
    data?: {
      error?: string
    }
    status?: number
  }
  message?: string
}

export interface DeviceLinkRequest {
  code: string
  expires_at: string
  poll_interval_seconds: number
}

export interface DeviceLinkStatus {
  is_approved: boolean
  user_name?: string
  approved_at?: string
}

export interface DeviceLinkApproval {
  code: string
  device_name?: string
  dashboard_id?: number
}

export interface DashboardInfo {
  dashboard_id: number
  name: string
  description: string
  competition_id?: number
}

export interface DeviceTokenRequest {
  code: string
}

export interface DeviceTokenResponse {
  access_token: string
  token_type: string
  expires_in?: number
  user_name: string
  platform_name: string
  platform_id: number
}

export interface DashboardData {
  message: string
  device_name?: string
  user_name: string
  platform_name: string
  authenticated_as: string
}

// Device linking flow API calls
export const requestDeviceCode = async (
  devicePlatform?: string
): Promise<DeviceLinkRequest> => {
  const response = await axios.post('/api/device-link/request', {
    device_platform: devicePlatform ?? null,
  })
  return response.data
}

export const checkDeviceStatus = async (
  code: string
): Promise<DeviceLinkStatus> => {
  const response = await axios.get(`/api/device-link/status/${code}`)
  return response.data
}

export const approveDeviceLink = async (approval: DeviceLinkApproval) => {
  const response = await axios.post('/api/device-link/approve', approval)
  return response.data
}

export const exchangeDeviceToken = async (
  request: DeviceTokenRequest
): Promise<DeviceTokenResponse> => {
  const response = await axios.post('/api/device-link/token', request)
  return response.data
}

// Test dashboard access with device token
export const getDashboardData = async (
  token: string
): Promise<DashboardData> => {
  const response = await axios.get('/api/device-link/dashboard', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response.data
}

// List all dashboards for the platform
const listDashboards = async (): Promise<DashboardInfo[]> => {
  const response = await axios.get('/api/device-link/dashboards')
  return response.data
}

export const useDashboards = () => {
  return useSuspenseQuery({
    queryKey: ['dashboards'],
    queryFn: listDashboards,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Get current dashboard for device
export const getCurrentDashboard = async (
  token: string
): Promise<DashboardInfo | null> => {
  const response = await axios.get('/api/device-link/dashboard/current', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response.data
}
